import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { BackofficeCreditApplicationV1Dto } from '../../../api/swagger/definitions/backoffice'
import Colors from '../../../figma/panda/Colors'
import {
  deriveJourneySteps,
  formatApplicationDate,
  type JourneyStep,
  requestStatusChipColor,
  requestStatusLabel,
  type StepState,
} from './creditApplicationHelpers'

const STEP_COLORS: Record<StepState, string> = {
  done: Colors.success700,
  active: Colors.alert400,
  failed: Colors.notificationErrors600,
  todo: Colors.base400,
}

const Step = ({ step, isLast }: { step: JourneyStep; isLast: boolean }) => (
  <Stack direction="row" spacing={1.5} sx={{ pb: isLast ? 0 : 1.5 }}>
    <Stack
      sx={{
        alignItems: 'center',
        alignSelf: 'stretch',
      }}
    >
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          backgroundColor: step.state === 'todo' ? Colors.baseWhite : STEP_COLORS[step.state],
          border: `2px solid ${STEP_COLORS[step.state]}`,
          flexShrink: 0,
          mt: '2px',
        }}
      />
      {!isLast && <Box sx={{ width: 2, flexGrow: 1, backgroundColor: Colors.base300, my: 0.5 }} />}
    </Stack>
    <Box sx={{ pt: '1px' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {step.title}
      </Typography>
      <Typography variant="body2" sx={{ color: step.state === 'todo' ? 'text.disabled' : 'text.secondary' }}>
        {step.detail}
      </Typography>
    </Box>
  </Stack>
)

const CreditApplicationCard = ({ application }: { application: BackofficeCreditApplicationV1Dto }) => {
  const steps = deriveJourneySteps(application)
  const { bg, color } = requestStatusChipColor(application.requestStatus)

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Chip
          label={requestStatusLabel(application.requestStatus)}
          size="small"
          sx={{ backgroundColor: bg, color, fontWeight: 600, fontSize: 11, height: 22, letterSpacing: 0.3 }}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {formatApplicationDate(application.createdAt)}
        </Typography>
      </Stack>

      {steps.map((step, index) => (
        <Step key={step.title} step={step} isLast={index === steps.length - 1} />
      ))}
    </Box>
  )
}

export default CreditApplicationCard
